import { useState } from 'react';
import { SectionTitle, GoldBadge } from '@/components/Luxury';
import { formatCurrency, formatPercent } from '../../utils/formatters';
import { MetricLabel } from './MetricLabel';
import type { FourScenarioComparison, ExitScenarioResult, CumulativeReturnBreakdown } from '../../types/calculations';

interface FourScenarioMatrixProps {
  comparison: FourScenarioComparison;
}

type HoldPeriod = '5yr' | '10yr';

function getIrr(scenario: ExitScenarioResult, period: HoldPeriod): number | undefined {
  return period === '5yr' ? scenario.irr_5yr : scenario.irr_10yr;
}

function getEquityMultiple(scenario: ExitScenarioResult, period: HoldPeriod): number | undefined {
  return period === '5yr' ? scenario.equity_multiple_5yr : scenario.equity_multiple_10yr;
}

function getBreakdown(scenario: ExitScenarioResult, period: HoldPeriod): CumulativeReturnBreakdown | null | undefined {
  return period === '5yr' ? scenario.return_breakdown_5yr : scenario.return_breakdown_10yr;
}

function ScenarioCell({
  scenario,
  period,
  isBest,
  isWorst,
  baselineIrr,
}: {
  scenario: ExitScenarioResult;
  period: HoldPeriod;
  isBest: boolean;
  isWorst: boolean;
  baselineIrr?: number;
}) {
  const irr = getIrr(scenario, period);
  const eqMult = getEquityMultiple(scenario, period);
  const is1031 = scenario.exit_strategy === '1031';

  // Calculate pp boost relative to baseline (clean_no_bonus in same row's non-bonus column)
  const ppBoost = irr != null && baselineIrr != null ? (irr - baselineIrr) * 100 : null;

  return (
    <td
      className={`p-4 ${isBest ? 'border-2 border-gold' : 'border border-slate-dark'} ${isWorst ? 'opacity-60' : ''}`}
    >
      <div className="space-y-1">
        {/* IRR */}
        <div className="flex items-center gap-2">
          <span className="text-lg font-mono tabular-nums text-ivory">
            {irr != null ? formatPercent(irr, 1) : 'N/A'}
          </span>
          {ppBoost != null && ppBoost > 0 && (
            <span className="text-sm text-emerald font-mono tabular-nums">(+{ppBoost.toFixed(1)}pp)</span>
          )}
          {isBest && <GoldBadge variant="gold" className="!text-sm !px-2 !py-0.5">&#9733; BEST</GoldBadge>}
        </div>

        {/* Equity Multiple */}
        <div className="text-sm text-stone-700 font-mono tabular-nums">
          {eqMult != null ? `${eqMult.toFixed(2)}x` : 'N/A'}
        </div>

        {/* Tax line */}
        {is1031 ? (
          <div className="space-y-0.5">
            <div className="text-sm text-emerald">
              Deferred: {formatCurrency(scenario.deferred_taxes_5yr)}
            </div>
            <div className="text-sm text-stone-700">QI Fee: $1,500</div>
          </div>
        ) : (
          <div className="text-sm text-crimson">
            Tax: -{formatCurrency(scenario.exit_taxes_5yr != null ? Math.abs(scenario.exit_taxes_5yr) : 0)}
          </div>
        )}

        {/* Return decomposition (Phase 169) */}
        {(() => {
          const bd = getBreakdown(scenario, period);
          if (!bd) return null;
          return (
            <div className="mt-2 pt-2 border-t border-white/10 space-y-0.5">
              <div className="flex justify-between text-sm">
                <span className="text-ivory/50">Cash Flow</span>
                <span className="font-mono tabular-nums text-ivory/70">{formatCurrency(bd.cumulative_cash_flow)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-ivory/50">Tax Benefits</span>
                <span className="font-mono tabular-nums text-amber-300/70">{formatCurrency(bd.cumulative_tax_benefits)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-ivory/50">Sale Proceeds</span>
                <span className="font-mono tabular-nums text-emerald/70">{formatCurrency(bd.net_sale_proceeds)}</span>
              </div>
              <div className="flex justify-between text-sm pt-0.5 border-t border-white/5">
                <span className="text-ivory/70 font-semibold">Total</span>
                <span className="font-mono tabular-nums text-ivory font-semibold">{formatCurrency(bd.total_return)}</span>
              </div>
            </div>
          );
        })()}
      </div>
    </td>
  );
}

export function FourScenarioMatrix({ comparison }: FourScenarioMatrixProps) {
  const [period, setPeriod] = useState<HoldPeriod>('5yr');

  if (!comparison) return null;

  const {
    clean_no_bonus,
    clean_with_bonus,
    exchange_no_bonus,
    exchange_with_bonus,
    best_scenario_label,
    worst_scenario_label,
    irr_spread_5yr,
  } = comparison;

  const isBest = (s: ExitScenarioResult) => s.label === best_scenario_label;
  const isWorst = (s: ExitScenarioResult) => s.label === worst_scenario_label;

  // Baseline IRRs for pp boost calculation (no-bonus in same row)
  const cleanBaseIrr = getIrr(clean_no_bonus, period);
  const exchangeBaseIrr = getIrr(exchange_no_bonus, period);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <SectionTitle>Return Scenarios</SectionTitle>
        <div className="flex gap-1">
          <button
            onClick={() => setPeriod('5yr')}
            className={`px-3 py-1 rounded text-sm transition-colors ${
              period === '5yr'
                ? 'bg-gold text-obsidian font-semibold'
                : 'bg-charcoal text-stone-700 hover:text-ivory'
            }`}
          >
            5yr
          </button>
          <button
            onClick={() => setPeriod('10yr')}
            className={`px-3 py-1 rounded text-sm transition-colors ${
              period === '10yr'
                ? 'bg-gold text-obsidian font-semibold'
                : 'bg-charcoal text-stone-700 hover:text-ivory'
            }`}
          >
            10yr
          </button>
        </div>
      </div>

      {/* Desktop 2x2 Grid */}
      <div className="hidden md:block overflow-x-auto rounded-lg border border-slate-dark">
        <table className="w-full">
          <thead>
            <tr className="bg-charcoal">
              <th className="p-3 text-left text-sm uppercase tracking-wider text-stone-700 border border-slate-dark w-36" />
              <th className="p-3 text-center text-sm uppercase tracking-wider text-stone-700 border border-slate-dark">
                No Bonus
              </th>
              <th className="p-3 text-center text-sm uppercase tracking-wider text-stone-700 border border-slate-dark">
                OBBB Bonus
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Clean Exit Row */}
            <tr>
              <td className="p-4 bg-charcoal border border-slate-dark">
                <div className="text-sm font-semibold text-ivory">Clean Exit</div>
                <div className="text-sm text-stone-700">(Full Tax)</div>
              </td>
              <ScenarioCell
                scenario={clean_no_bonus}
                period={period}
                isBest={isBest(clean_no_bonus)}
                isWorst={isWorst(clean_no_bonus)}
              />
              <ScenarioCell
                scenario={clean_with_bonus}
                period={period}
                isBest={isBest(clean_with_bonus)}
                isWorst={isWorst(clean_with_bonus)}
                baselineIrr={cleanBaseIrr}
              />
            </tr>
            {/* 1031 Exchange Row */}
            <tr>
              <td className="p-4 bg-charcoal border border-slate-dark">
                <div className="text-sm font-semibold text-ivory">1031 Exchange</div>
                <div className="text-sm text-stone-700">(Deferred)</div>
              </td>
              <ScenarioCell
                scenario={exchange_no_bonus}
                period={period}
                isBest={isBest(exchange_no_bonus)}
                isWorst={isWorst(exchange_no_bonus)}
              />
              <ScenarioCell
                scenario={exchange_with_bonus}
                period={period}
                isBest={isBest(exchange_with_bonus)}
                isWorst={isWorst(exchange_with_bonus)}
                baselineIrr={exchangeBaseIrr}
              />
            </tr>
          </tbody>
        </table>
      </div>

      {/* Mobile stacked cards */}
      <div className="md:hidden space-y-3">
        {[clean_no_bonus, clean_with_bonus, exchange_no_bonus, exchange_with_bonus].map((scenario) => {
          const irr = getIrr(scenario, period);
          const eqMult = getEquityMultiple(scenario, period);
          const best = isBest(scenario);
          const worst = isWorst(scenario);
          const is1031 = scenario.exit_strategy === '1031';

          return (
            <div
              key={scenario.label}
              className={`p-4 rounded-lg border ${best ? 'border-2 border-gold' : 'border-slate-dark'} ${worst ? 'opacity-60' : ''} bg-charcoal`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-ivory">{scenario.label}</span>
                {best && <GoldBadge variant="gold" className="!text-sm !px-2 !py-0.5">&#9733; BEST</GoldBadge>}
              </div>
              <div className="flex items-baseline gap-3">
                <span className="text-lg font-mono tabular-nums text-ivory">
                  {irr != null ? formatPercent(irr, 1) : 'N/A'}
                </span>
                <span className="text-sm text-stone-700 font-mono tabular-nums">
                  {eqMult != null ? `${eqMult.toFixed(2)}x` : 'N/A'}
                </span>
              </div>
              {is1031 ? (
                <div className="mt-1 space-y-0.5">
                  <div className="text-sm text-emerald">Deferred: {formatCurrency(scenario.deferred_taxes_5yr)}</div>
                  <div className="text-sm text-stone-700">QI Fee: $1,500</div>
                </div>
              ) : (
                <div className="mt-1 text-sm text-crimson">
                  Tax: -{formatCurrency(scenario.exit_taxes_5yr != null ? Math.abs(scenario.exit_taxes_5yr) : 0)}
                </div>
              )}
              {/* Return decomposition (Phase 169) */}
              {(() => {
                const bd = getBreakdown(scenario, period);
                if (!bd) return null;
                return (
                  <div className="mt-2 pt-2 border-t border-white/10 space-y-0.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-ivory/50">Cash Flow</span>
                      <span className="font-mono tabular-nums text-ivory/70">{formatCurrency(bd.cumulative_cash_flow)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-ivory/50">Tax Benefits</span>
                      <span className="font-mono tabular-nums text-amber-300/70">{formatCurrency(bd.cumulative_tax_benefits)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-ivory/50">Sale Proceeds</span>
                      <span className="font-mono tabular-nums text-emerald/70">{formatCurrency(bd.net_sale_proceeds)}</span>
                    </div>
                    <div className="flex justify-between text-sm pt-0.5 border-t border-white/5">
                      <span className="text-ivory/70 font-semibold">Total</span>
                      <span className="font-mono tabular-nums text-ivory font-semibold">{formatCurrency(bd.total_return)}</span>
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })}
      </div>

      {/* Insight bar */}
      <div className="border-l-4 border-gold bg-obsidian/50 p-4 rounded-r-lg">
        <div className="flex flex-wrap items-center gap-4 text-sm">
          {irr_spread_5yr != null && (
            <span className="font-mono tabular-nums text-gold font-semibold">
              SPREAD: +{(irr_spread_5yr * 100).toFixed(1)}pp <MetricLabel metric="irr">IRR</MetricLabel>
            </span>
          )}
          <span className="text-stone-700">
            Best: <span className="text-ivory">{best_scenario_label}</span>
          </span>
          <span className="text-stone-700">
            Year 1 OBBB Benefit:{' '}
            <span className="text-emerald">{formatCurrency(exchange_with_bonus.year1_tax_benefit)}</span>
          </span>
        </div>
      </div>
    </div>
  );
}
